import {
  Chip,
  Box,
  Card,
  CardContent,
  Sheet,
  Table,
  Typography,
  Stack,
  Link,
} from "@mui/joy";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import { useTranslation } from "../../../../i18n";
import { useServiceTypeOptions } from "../hooks/useServiceTypeOptions";
import { renderIcon, useStatusOptions } from "../hooks/useStatusOptions";
import { useVisibilityOptions } from "../hooks/useVisibilityOptions";
import QSelect from "../../../../x-components/QSelect";
import QDatePicker from "../../../../x-components/QDatePicker";
import { Link as RouterLink } from "react-router-dom";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import {
  InspectionStatus,
  UpdateInspectionInput,
  useInspectionDetailsByRefQuery,
  useInspectionDetailsUpdateMutation,
  InspectionResult,
} from "../../../../__generated__/types-and-hooks";
import QTextInput from "../../../../x-components/QTextInput";
import QNumberInput from "../../../../x-components/QNumberInput";
import { useTeam } from "../../TeamLayout";
import CountryFlag from "../../../../components/CountryFlag";
import QSupplier from "../../../../x-components/QSupplier";
import { QAgency } from "../../../../x-components/QAgency";
import { QMoneyInput } from "../../../../x-components/QMoneyInput";
import { useParams } from "react-router-dom";
import { FilesCard } from "../../../../components/FilesCard";
import { useInspectionResultOptions } from "../hooks/useInspectionResultOptions";

const iconMap: Record<string, React.ReactNode> = {
  phone: <Phone style={{ fontSize: 14 }} />,
  email: <Email style={{ fontSize: 14 }} />,
  whatsapp: <WhatsApp style={{ fontSize: 14 }} />,
};

export default function Details() {
  const { ref } = useParams();
  const inspectionResultOptions = useInspectionResultOptions();
  const inspectionQuery = useInspectionDetailsByRefQuery({
    variables: { uniqueId: ref! },
  });
  const inspection = inspectionQuery.data?.inspection_by_unique_id;
  const editable = new Set(inspection?.editableFields ?? []);
  const [inspectionUpdateMutation] = useInspectionDetailsUpdateMutation({});
  const teamCtx = useTeam();
  const t = useTranslation();
  const serviceTypeOptions = useServiceTypeOptions();
  const statusOptions = useStatusOptions();
  const status = statusOptions.find((opt) => opt.value === inspection?.status);
  const visibilityOptions = useVisibilityOptions();
  const visibility = visibilityOptions.find(
    (opt) => opt.value === inspection?.visibility
  );

  const updateFn = async (updates: Omit<UpdateInspectionInput, "uniqueId">) => {
    const res = await inspectionUpdateMutation({
      variables: {
        updates: {
          uniqueId: inspection?.uniqueId!,
          ...updates,
        },
      },
      onCompleted(data) {
        inspectionQuery.refetch();
      },
    });
    return res.errors?.map((err) => err.message).join("; ");
  };

  const supplier = inspection?.supplier;

  return (
    <Box
      sx={{
        p: 2,
        columns: "400px",
      }}
    >
      {inspection && (
        <Card
          sx={{
            backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
            padding: 0,
            mb: 2,
            breakInside: "avoid",
          }}
          variant="outlined"
        >
          <Typography
            sx={{ pt: 1, pl: 2 }}
            level="title-lg"
            startDecorator={visibility?.icon}
            endDecorator={status ? renderIcon(status.value) : null}
          >
            {t("Inspection")}
          </Typography>
          <CardContent
            sx={{
              m: 0,
              margin: "2px",
              borderRadius: 5,
              border: "1px solid black",
              bgcolor: "background.surface",
              gap: 0,
              p: 1,
              pl: 2,
            }}
          >
            <Sheet sx={{ overflow: "auto" }}>
              <Table
                borderAxis="xBetween"
                sx={{
                  p: 0,
                  m: 0,
                  "& tbody td:nth-child(1)": { width: 180 },
                  "& tbody td": { pl: 0 },
                }}
              >
                <tbody>
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Service Type")}
                      </Typography>
                    </td>
                    <td>
                      <QSelect
                        readonly={!editable.has("serviceType")}
                        val={inspection.serviceType ?? null}
                        options={serviceTypeOptions}
                        update={(serviceType) => updateFn({ serviceType })}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Status")}</Typography>
                    </td>
                    <td>
                      <QSelect
                        readonly={!editable.has("status")}
                        val={inspection.status}
                        options={statusOptions}
                        update={(status) =>
                          updateFn({ status: status as InspectionStatus })
                        }
                      />
                    </td>
                  </tr>
                  {inspection.status === InspectionStatus.Cancelled && (
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Cancellation Reason")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("cancellationReason")}
                          val={inspection.cancellationReason ?? ""}
                          update={(cancellationReason) =>
                            updateFn({
                              cancellationReason: cancellationReason,
                            })
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {/* {inspection.status === InspectionStatus.Draft && (
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Visibility")}
                        </Typography>
                      </td>
                      <td>
                        <QSelect
                          readonly={!editable.has("visibility")}
                          val={inspection.visibility ?? null}
                          options={visibilityOptions}
                          update={(visibility) =>
                            updateFn({
                              visibility: visibility as InspectionVisibility,
                            })
                          }
                        />
                      </td>
                    </tr>
                  )} */}
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Service Date Start")}
                      </Typography>
                    </td>
                    <td>
                      <QDatePicker
                        readonly={!editable.has("startDate")}
                        val={inspection.startDate}
                        update={(startDate) =>
                          updateFn({
                            startDate: startDate,
                          })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Service Date End")}
                      </Typography>
                    </td>
                    <td>
                      <QDatePicker
                        readonly={!editable.has("endDate")}
                        val={inspection.endDate}
                        update={(endDate) =>
                          updateFn({
                            endDate: endDate,
                          })
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Service Days")}
                      </Typography>
                    </td>
                    <td>
                      <QNumberInput
                        // readonly={!editable.has("estimatedServiceDays")}
                        val={10}
                        update={(serviceDays) =>
                          updateFn({
                            serviceDays: serviceDays,
                          })
                        }
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Total Costs")}
                      </Typography>
                    </td>
                    <td>
                      <QMoneyInput
                        readonly={
                          !editable.has("costTotal") ||
                          !editable.has("costCurrency")
                        }
                        val={[
                          inspection.costTotal ?? 0,
                          inspection.costCurrency ?? "USD",
                        ]}
                        update={(data) => {
                          const [costTotal, costCurrency] = data ?? [0, "USD"];
                          return updateFn({ costTotal, costCurrency });
                        }}
                      />
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <Typography level="body-sm">{t("$$$ Memo")}</Typography>
                    </td>
                    <td>
                      <QTextInput
                        val={inspection.costMemo ?? ""}
                        update={(costMemo) => updateFn({ costMemo: costMemo })}
                      />
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Sheet>
          </CardContent>
        </Card>
      )}
      {inspection && (
        <Card
          sx={{
            backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
            padding: 0,
            mb: 2,
            breakInside: "avoid",
          }}
          variant="outlined"
        >
          <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
            {t("Supplier")}
          </Typography>
          <CardContent
            sx={{
              m: 0,
              margin: "2px",
              borderRadius: 5,
              border: "1px solid black",
              bgcolor: "background.surface",
              gap: 0,
              p: 0,
            }}
          >
            <Sheet sx={{ overflow: "auto" }}>
              <Table
                borderAxis="xBetween"
                sx={{
                  pt: 2,
                  pl: 2,
                  pr: 1,
                  m: 0,
                  "& tbody td:nth-child(1)": { width: 180 },
                  "& tbody td": { pl: 0 },
                }}
              >
                <tbody>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Supplier")}</Typography>
                    </td>
                    <td>
                      <QSupplier
                        teamId={teamCtx.team.id}
                        supplier={inspection?.supplier ?? null}
                        update={(s) =>
                          updateFn({
                            supplierId: s?.id ?? -1,
                          })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Address Line 1")}
                      </Typography>
                    </td>
                    <td>
                      <Typography>{supplier?.addressLine1}</Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Address Line 2")}
                      </Typography>
                    </td>
                    <td>
                      <Typography>{supplier?.addressLine2}</Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("City")}</Typography>
                    </td>
                    <td>
                      <Typography>{supplier?.city}</Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">
                        {t("Postal Code")}
                      </Typography>
                    </td>
                    <td>
                      <Typography>{supplier?.postalCode}</Typography>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Country")}</Typography>
                    </td>
                    <td>
                      {supplier?.countryCode && (
                        <CountryFlag code={supplier.countryCode} />
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <Stack gap={0.5} sx={{ mb: 0.5 }}>
                {supplier?.contacts?.map((contact) => (
                  <Card
                    variant="outlined"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      padding: 1.5,
                      ml: 0.5,
                      mr: 0.5,
                      backgroundColor: "hsl(0deg 0% 31.06% / 28%);",
                    }}
                  >
                    <Typography level="title-sm">{contact?.name}</Typography>
                    {contact.contactMethods.map((method, index) => (
                      <Chip
                        key={index}
                        variant="soft"
                        sx={{ borderRadius: 4 }}
                        startDecorator={
                          iconMap[method.key] ?? (
                            <Message style={{ fontSize: 14 }} />
                          )
                        }
                      >
                        {method.value}
                      </Chip>
                    ))}
                    <KeyValueLine
                      label={t("Address Line 1")}
                      value={contact.addressLine1}
                    />
                    <KeyValueLine
                      label={t("Address Line 2")}
                      value={contact.addressLine2}
                    />
                    <KeyValueLine label={t("City")} value={contact.city} />
                    <KeyValueLine label={t("State")} value={contact.state} />
                    <KeyValueLine
                      label={t("Postal Code")}
                      value={contact.postalCode}
                    />
                    <KeyValueLine
                      label={t("Country")}
                      value={
                        contact.countryCode ? (
                          <CountryFlag code={contact.countryCode} />
                        ) : null
                      }
                    />
                  </Card>
                ))}
              </Stack>
            </Sheet>
          </CardContent>
        </Card>
      )}
      {inspection && (
        <Card
          sx={{
            backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
            padding: 0,
            mb: 2,
            breakInside: "avoid",
          }}
          variant="outlined"
        >
          <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
            {t("Agency")}
          </Typography>
          <CardContent
            sx={{
              m: 0,
              margin: "2px",
              borderRadius: 5,
              border: "1px solid black",
              bgcolor: "background.surface",
              gap: 0,
              p: 1,
              pl: 2,
            }}
          >
            <Sheet sx={{ overflow: "auto" }}>
              <Table
                borderAxis="xBetween"
                sx={{
                  p: 0,
                  m: 0,
                  "& tbody td:nth-child(1)": { width: 180 },
                  "& tbody td": { pl: 0 },
                }}
              >
                <tbody>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Agency")}</Typography>
                    </td>
                    <td>
                      <QAgency
                        agency={inspection?.agency ?? null}
                        update={(agency) =>
                          updateFn({ agencyId: agency?.id ?? -1 })
                        }
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Link")}</Typography>
                    </td>
                    <td>
                      {inspection?.agency && (
                        <Link
                          component={RouterLink}
                          to={`/network/${inspection?.agency?.slug}`}
                          target={"_blank"}
                          endDecorator={<OpenInNewRounded />}
                        >
                          {inspection?.agency?.slug}
                        </Link>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Typography level="body-sm">{t("Contacts")}</Typography>
                    </td>
                    <td>
                      {(inspection?.agency?.contacts ?? []).map(
                        (contact, index) => (
                          <Typography key={index}>
                            {contact.key} {contact.value}
                          </Typography>
                        )
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Sheet>
          </CardContent>
        </Card>
      )}
      {inspection && (
        <>
          <Card
            sx={{
              backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
              padding: 0,
              mb: 2,
              breakInside: "avoid",
            }}
            variant="outlined"
          >
            <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
              {t("Final Report")}
            </Typography>
            <CardContent
              sx={{
                m: 0,
                margin: "2px",
                borderRadius: 5,
                border: "1px solid black",
                bgcolor: "background.surface",
                gap: 0,
                p: 1,
                pl: 2,
              }}
            >
              <Sheet sx={{ overflow: "auto" }}>
                <Table
                  borderAxis="xBetween"
                  sx={{
                    p: 0,
                    m: 0,
                    "& tbody td:nth-child(1)": { width: 180 },
                    "& tbody td": { pl: 0 },
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Result")}</Typography>
                      </td>
                      <td>
                        <QSelect
                          val={inspection?.result ?? null}
                          options={inspectionResultOptions}
                          update={async (result) =>
                            updateFn({
                              result: result as InspectionResult,
                            })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Inspected by")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={inspection?.inspectedBy ?? ""}
                          update={(inspectedBy) =>
                            updateFn({
                              inspectedBy: inspectedBy,
                            })
                          }
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Inspector Notes")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          val={inspection?.inspectorNotes ?? ""}
                          update={(inspectorNotes) =>
                            updateFn({
                              inspectorNotes: inspectorNotes,
                            })
                          }
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
          <FilesCard entityId={inspection?.uniqueId} entityType="inspection" />
        </>
      )}
    </Box>
  );
}

function KeyValueLine({
  label,
  value,
}: {
  label: string;
  value?: string | null | React.ReactNode;
}) {
  if (!value) return null;
  return (
    <Stack direction="row" gap={1}>
      <Typography sx={{ minWidth: 140 }} level="body-md">
        {label}:
      </Typography>
      <Typography>{value}</Typography>
    </Stack>
  );
}
